import React, { useState } from 'react';
import { Checkbox, Tooltip } from '@mui/material';
import { type ApolloQueryResult } from '@apollo/react-hooks';
import {
  moreBox,
  MemoizedPanelPublication,
  MemoizedPanelRemarks,
  MemoizedPanelDegree,
  MemoizedPanelResult,
  MemoizedPanelTags,
  MemoizedPanelReviewers,
  MemoizedPanelMerit,
  MemoizedPanelEvaluationScore,
  offeredProgrammeMap,
} from '.';
import {
  panelApplicantListPermissionConfig,
  panelFundingConfig,
} from './panelApplicantListPermissionConfig';
import { PANEL_DETAIL_APPLICATION_TEXT } from '../../../../lang';
import {
  type PanelApplication,
  useUpdatePanelApplicationItem,
  useUpdateRemarkOnApplication,
  type DimensionsCollection,
} from '../../../../graphql/GSAssessment';
import {
  type HeadCell,
  type AlertDetails,
  Alert,
  getUserRoleByRoleModule,
  UserRole,
  type ROLES_MODULE,
  VirtualizedTable,
  type SelectOption,
} from '../../../../../../components';
import {
  FundingSource,
  SYSTEM_SCORE,
} from '../../../../../Nomination/components';
import { useAuth } from '../../../../../../contexts';

type ApplicationListProps = {
  panelId: string;
  viewSituation: ViewSituation;
  panelApplications: PanelApplication[];
  fundingSourceEnum: string;
  tagOptions: string[];
  panelSubmitted: boolean;
  triggerRender: number;
  dimensionsCollection: DimensionsCollection[];
  panelResultOptions: SelectOption[];
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

// GS: gs member can view and edit everything except for panel member review
// MEMBER_NORMAL: panel member can view info of each applicant, and do specific review, but cannot edit list
// MEMBER_REVIEWEE_ONLY: panel member can only view specific applicants
export enum ViewSituation {
  GS = 'GS',
  MEMBER_NORMAL = 'MEMBER_NORMAL',
  MEMBER_REVIEWEE_ONLY = 'MEMBER_REVIEWEE_ONLY',
}

export const ApplicationList: React.FunctionComponent<ApplicationListProps> = (
  props: ApplicationListProps,
) => {
  const {
    viewSituation,
    panelSubmitted,
    panelApplications,
    fundingSourceEnum,
    panelId,
    tagOptions,
    dimensionsCollection,
    panelResultOptions,
    triggerRender,
    handleLoading,
    refetch,
  } = props;
  const { user, role: currentRole } = useAuth();
  const { enableEditColumns, hideTopTen, hideSerialNo, showIndex } =
    panelApplicantListPermissionConfig[viewSituation];
  const isHKPFS =
    fundingSourceEnum === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME;

  const {
    showReviewer,
    showRatingDetails,
    showDeptPriorityNo,
    showCSStudentshipYears,
    showDiscipline,
    showOfferedProgramme,
    showPartnerUniversity,
  } = panelFundingConfig(fundingSourceEnum as FundingSource);
  const tableDisabled = panelSubmitted || !enableEditColumns;

  const currentNetId = user?.sub ?? '';
  const currentRoleModule = getUserRoleByRoleModule(
    currentRole as ROLES_MODULE,
  );
  const scoringSystem: SYSTEM_SCORE = isHKPFS
    ? SYSTEM_SCORE.HKPFS
    : SYSTEM_SCORE.UPAF;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const topTenCell: HeadCell<PanelApplication> = {
    id: 'topTen',
    label: PANEL_DETAIL_APPLICATION_TEXT.TOP_TEN,
    isSortable: false,
    minWidth: 8,
    render: (row: PanelApplication) => (
      <Checkbox
        key={triggerRender}
        checked={row.isTopTen}
        size="small"
        disabled={tableDisabled}
        onChange={async (e, checked) => {
          await handleTickTopTen(row.applicationId, checked);
        }}
      />
    ),
  };

  const disciplineCell: HeadCell<PanelApplication> = {
    id: 'classification',
    label: PANEL_DETAIL_APPLICATION_TEXT.DISCIPLINE,
    isSortable: false,
    minWidth: 8,
    render: (row: PanelApplication) => row.classification,
  };

  const serialNoCell: HeadCell<PanelApplication> = {
    id: 'serialNo',
    label: PANEL_DETAIL_APPLICATION_TEXT.SERIAL_NUMBER,
    isSortable: false,
    minWidth: 4,
    render: (row: PanelApplication) => (
      <b className="text-[#951D1D]">{row.serialNo}</b>
    ),
  };

  const reviewerCell: Array<HeadCell<PanelApplication>> = [
    {
      id: 'reviewers',
      label: PANEL_DETAIL_APPLICATION_TEXT.REVIEWER,
      isSortable: false,
      minWidth: 20,
      render: (row: PanelApplication) => {
        const scholarshipScore = Number(
          row.scholarshipScores.find(item => item.funding === fundingSourceEnum)
            ?.score ?? 0,
        );
        return (
          <MemoizedPanelReviewers
            reviewers={row.reviewers}
            applicantName={row.applicantName}
            applicationNo={row.applicationNo}
            applicationId={row.applicationId}
            triggerRender={triggerRender}
            currentRole={currentRole}
            panelDimensionalScores={row.panelDimensionalScores}
            panelId={panelId}
            scholarshipScore={scholarshipScore}
            currentNetId={currentNetId}
            fundingSourceEnum={fundingSourceEnum}
            refetch={refetch}
            handleLoading={handleLoading}
          />
        );
      },
    },

    {
      id: 'avgScore',
      label: PANEL_DETAIL_APPLICATION_TEXT.AVG_REVIEW_SCORE,
      isSortable: false,
      minWidth: 6,
      render: (row: PanelApplication) => row.avgScore,
    },
  ];

  const deptPriorityNumberCell: HeadCell<PanelApplication> = {
    id: 'deptPriorityNumber',
    label: PANEL_DETAIL_APPLICATION_TEXT.DEPT_PRIORITY_NO,
    isSortable: false,
    minWidth: 6,
    render: (row: PanelApplication) => {
      const systemScore = Number(
        row.scholarshipScores.find(item => item.funding === fundingSourceEnum)
          ?.systemScore ?? 0,
      ).toFixed(2);
      const departmentScore = Number(
        row.scholarshipScores.find(item => item.funding === fundingSourceEnum)
          ?.score ?? 0,
      ).toFixed(2);
      return `${systemScore} | ${departmentScore} | ${row.deptPriorityNumber}`;
    },
  };

  const csStudentshipYearsCell: HeadCell<PanelApplication> = {
    id: 'csStudentshipYears',
    label: PANEL_DETAIL_APPLICATION_TEXT.CS_STUDENTSHIP_YEARS,
    isSortable: false,
    minWidth: 6,
    render: (row: PanelApplication) => row.csStudentshipYears,
  };
  const csRemainStudentshipYearsCell: HeadCell<PanelApplication> = {
    id: 'csRemainStudentshipYears',
    label: PANEL_DETAIL_APPLICATION_TEXT.REMAINING_STUDENTSHIP_YEAR,
    isSortable: false,
    minWidth: 6,
    render: (row: PanelApplication) => {
      const needYear = offeredProgrammeMap[row.offeredProgramme]?.duration ?? 0;
      const isOver = row.csRemainStudentshipYears < needYear;
      return (
        <Tooltip
          title={isOver ? PANEL_DETAIL_APPLICATION_TEXT.EXCEED_HINT : undefined}
        >
          <p className={`${isOver && 'font-bold text-[#951D1D]'}`}>
            {row.csRemainStudentshipYears}
            {isOver ? '\u2757' : ''}
          </p>
        </Tooltip>
      );
    },
  };

  const offeredProgrammeCell: HeadCell<PanelApplication> = {
    id: 'offeredProgramme',
    label: PANEL_DETAIL_APPLICATION_TEXT.OFFERED_PROGRAMME,
    isSortable: false,
    minWidth: 6,
    render: (row: PanelApplication) => {
      const matched = offeredProgrammeMap[row.offeredProgramme];
      return <>{matched ? matched.prog + '-' + matched.duration : 'N/A'}</>;
    },
  };
  const initialDataCells: Array<HeadCell<PanelApplication>> = [
    ...(hideSerialNo ? [] : [serialNoCell]),

    {
      id: 'applicationNo',
      label: showPartnerUniversity
        ? PANEL_DETAIL_APPLICATION_TEXT.DUAL_APPLICATION_NO
        : PANEL_DETAIL_APPLICATION_TEXT.APPLICATION_NO,
      isSortable: false,
      minWidth: 8,
      render: (row: PanelApplication) =>
        showPartnerUniversity ? (
          <div>
            {row.applicationNo}
            <hr className="my-2" />
            {row.partnerUniversity}
          </div>
        ) : (
          row.applicationNo
        ),
    },
    {
      id: 'applicantName',
      label: PANEL_DETAIL_APPLICATION_TEXT.NAME,
      isSortable: false,
      minWidth: 8,
      render: (row: PanelApplication) => (
        <div>
          <p>{row.applicantName}</p>
          {!isHKPFS && row.isHKPFSNominee && (
            <>
              <hr className="my-2" />
              <i className="text-[#951D1D]">{'*HKPFS Nominee'}</i>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'department',
      label: PANEL_DETAIL_APPLICATION_TEXT.DEPARTMENT,
      isSortable: false,
      minWidth: 6,
      render: (row: PanelApplication) => row.department,
    },

    ...(showDiscipline ? [disciplineCell] : []),

    {
      id: 'nationality',
      label: PANEL_DETAIL_APPLICATION_TEXT.NATIONALITY,
      isSortable: false,
      minWidth: 8,
      render: (row: PanelApplication) => row.nationality,
    },
    {
      id: 'firstDegreeInformation',
      label: PANEL_DETAIL_APPLICATION_TEXT.FIRST_DEGREE,
      isSortable: false,
      minWidth: 16,
      render: (row: PanelApplication) => (
        <MemoizedPanelDegree
          panelDegreeInformation={row.firstDegreeInformation}
        />
      ),
    },

    ...(hideTopTen ? [] : [topTenCell]),

    {
      id: 'tags',
      label: PANEL_DETAIL_APPLICATION_TEXT.TAGS,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelApplication) => (
        <MemoizedPanelTags
          applicationId={row.applicationId}
          currentTags={row.tags}
          disabled={tableDisabled}
          triggerRender={triggerRender}
          tagOptions={tagOptions}
          handlePreUpdateAddTag={handlePreUpdateAddTag}
        />
      ),
    },
    {
      id: 'panelResult',
      label: PANEL_DETAIL_APPLICATION_TEXT.RESULT,
      isSortable: false,
      minWidth: 10,
      render: (row: PanelApplication) => (
        <MemoizedPanelResult
          applicationId={row.applicationId}
          panelResult={row.panelResult}
          disabled={tableDisabled}
          triggerRender={triggerRender}
          panelResultOptions={panelResultOptions}
          handleChangePanelResult={handleChangePanelResult}
        />
      ),
    },
    ...(showReviewer ? reviewerCell : []),
    ...(showDeptPriorityNo ? [deptPriorityNumberCell] : []),
    ...(showCSStudentshipYears
      ? [csStudentshipYearsCell, csRemainStudentshipYearsCell]
      : []),
    ...(showOfferedProgramme ? [offeredProgrammeCell] : []),

    {
      id: 'panelRemarks',
      label: PANEL_DETAIL_APPLICATION_TEXT.REMARK_PANEL,
      isSortable: false,
      minWidth: showReviewer ? 10 : 15,
      render: (row: PanelApplication) => (
        <MemoizedPanelRemarks
          applicationId={row.applicationId}
          remarks={row.panelRemarks}
          disabled={tableDisabled}
          triggerNum={triggerRender}
          currentRoleModule={currentRoleModule}
          remarksToRole={UserRole.PANEL}
          handleSavePanelRemarks={handleSaveRemarks}
        />
      ),
    },
    {
      id: 'panelDimensionalScores',
      label: (
        <>
          {PANEL_DETAIL_APPLICATION_TEXT.RATING}
          <br />
          {PANEL_DETAIL_APPLICATION_TEXT.RATING_EXPLANATION}
        </>
      ),
      isSortable: false,
      collapsed: true,
      minWidth: 48,
      render: (row: PanelApplication) => (
        <MemoizedPanelEvaluationScore
          panelDimensionalScores={row.panelDimensionalScores}
          reviewers={row.reviewers}
          showRatingDetails={showRatingDetails}
          scoringSystem={scoringSystem}
          fundingSourceEnum={fundingSourceEnum}
          dimensionsCollection={dimensionsCollection}
        />
      ),
    },
    {
      id: 'publicationData',
      label: (
        <>
          {PANEL_DETAIL_APPLICATION_TEXT.PUBLICATION}
          <br />
          {PANEL_DETAIL_APPLICATION_TEXT.PUB_ACCEPTED}
          <br />
          <small>{PANEL_DETAIL_APPLICATION_TEXT.PUB_EXPLANATION}</small>
        </>
      ),
      isSortable: false,
      collapsed: true,
      minWidth: 30,
      render: (row: PanelApplication) => (
        <MemoizedPanelPublication publicationData={row.publicationData} />
      ),
    },
    {
      id: 'secondDegreeInformation',
      label: PANEL_DETAIL_APPLICATION_TEXT.SECOND_DEGREE,
      isSortable: false,
      minWidth: 26,
      collapsed: true,
      render: (row: PanelApplication) => (
        <MemoizedPanelDegree
          panelDegreeInformation={row.secondDegreeInformation}
        />
      ),
    },
    {
      id: 'remarksDorpgs',
      label: PANEL_DETAIL_APPLICATION_TEXT.REMARK_DORPGS,
      isSortable: false,
      collapsed: true,
      minWidth: 18,
      render: (row: PanelApplication) => (
        <MemoizedPanelRemarks
          applicationId={row.applicationId}
          remarks={row.remarksDorpgs}
          disabled={tableDisabled}
          triggerNum={triggerRender}
          currentRoleModule={currentRoleModule}
          remarksToRole={UserRole.DO_RPG_S}
          handleSavePanelRemarks={handleSaveRemarks}
        />
      ),
    },
    {
      id: 'merits',
      label: PANEL_DETAIL_APPLICATION_TEXT.MERIT,
      isSortable: false,
      collapsed: true,
      minWidth: 20,
      render: (row: PanelApplication) => (
        <MemoizedPanelMerit merits={row.merits} />
      ),
    },
    {
      id: 'more',
      label: PANEL_DETAIL_APPLICATION_TEXT.VIEW_PROFILE,
      isSortable: false,
      collapsed: true,
      minWidth: 10,
      render: (row: PanelApplication) => moreBox(row.applicationId),
    },
  ];

  const updatePanelApplicationItem = useUpdatePanelApplicationItem();
  const updateRemarkOnApplication = useUpdateRemarkOnApplication();
  const handlePreUpdateAddTag = async (
    applicationId: string,
    action: 'add' | 'delete',
    currentTags: string[],
    tag: string,
  ): Promise<void> => {
    let newTags: string[] = [];
    switch (action) {
      case 'add':
        newTags = [...currentTags, tag];
        break;
      case 'delete':
        newTags = [...currentTags.filter(item => item !== tag)];
        break;
      default:
        break;
    }
    await handleUpdateTags(applicationId, newTags);
  };

  const handleUpdateTags = async (
    applicationId: string,
    tags: string[],
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          input: {
            panelId,
            applicationId,
            tags,
          },
        };
        await updatePanelApplicationItem({ variables });
        await refetch();
        setAlertDetails({
          alertContent: 'Updated Tag(s) Successfully.',
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent: 'Failed to Update Tag(s).' + (error as Error).message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleSaveRemarks = async (
    applicationId: string,
    remarks: string,
    toRole: UserRole.PANEL | UserRole.DO_RPG_S,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          input: {
            panelId,
            netId: currentNetId,
            applicationId,
            remarks,
            role: toRole,
          },
        };
        await updateRemarkOnApplication({ variables });
        await refetch();
        setAlertDetails({
          alertContent: 'Saved Remarks Successfully.',
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent: 'Failed to Save Remarks.' + (error as Error).message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleTickTopTen = async (
    applicationId: string,
    tick: boolean,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          input: {
            panelId,
            applicationId,
            isTop10: tick,
          },
        };
        await updatePanelApplicationItem({ variables });
        await refetch();
        setAlertDetails({
          alertContent: `${tick ? 'Ticked' : 'Unticked'} the Top 10% Flag Successfully.`,
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent:
            `Failed to ${tick ? 'Tick' : 'Untick'} the Top 10% Flag.` +
            (error as Error).message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleChangePanelResult = async (
    applicationId: string,
    panelResult: string,
  ): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          input: {
            panelId,
            applicationId,
            panelResult,
          },
        };
        await updatePanelApplicationItem({ variables });
        await refetch();
        setAlertDetails({
          alertContent: `Updated the Panel Result Successfully.`,
          alertType: 'success',
        });
      } catch (error) {
        setAlertDetails({
          alertContent:
            `Failed to Update the Panel Result.` + (error as Error).message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const handleRequestSort = (property: keyof PanelApplication): void => {
    // TODO: Get the sorted data from the server
  };

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      <div style={{ minWidth: '80rem' }}>
        <VirtualizedTable<PanelApplication>
          defaultCollapsed={false}
          headCells={initialDataCells}
          showIndex={showIndex}
          data={panelApplications}
          handleRequestSort={handleRequestSort}
        />
      </div>
    </div>
  );
};
